import { ArrowRight } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n'
import { Banner } from '@/ui'
import { PAYOUT_ACTION_TEXT, type CorrectionPreview } from '../model/lotMath'
import { useScope } from '../api/appConfig'

/**
 * The money effect of a correction before it is saved (F4.10-h): the bag and the lot before and
 * after, the signed difference for the farmer, and what happens to his payment.
 */
export function MoneyEffectPreview({ preview }: { preview: CorrectionPreview }) {
  const { v1 } = useScope()
  const fmt = useFormat()
  const { before, after, adjustmentPaise } = preview
  const rows: Array<{ label: string; a: string; b: string; changed: boolean }> = []
  if (preview.bagBefore && preview.bagAfter) {
    rows.push(
      {
        label: 'Bag gross',
        a: fmt.weight(preview.bagBefore.grossG),
        b: fmt.weight(preview.bagAfter.grossG),
        changed: preview.bagBefore.grossG !== preview.bagAfter.grossG,
      },
      {
        label: 'Bag sack weight',
        a: fmt.weight(preview.bagBefore.tareG),
        b: fmt.weight(preview.bagAfter.tareG),
        changed: preview.bagBefore.tareG !== preview.bagAfter.tareG,
      },
    )
  }
  rows.push(
    {
      label: 'Lot net',
      a: fmt.weight(before.netG),
      b: fmt.weight(after.netG),
      changed: before.netG !== after.netG,
    },
    // V1 cards carry no deductions: payable is the net weight, so the two rows say nothing.
    ...(v1
      ? []
      : [
          {
            label: 'Deduction',
            a: `${(before.deductionBp / 100).toFixed(2)} %`,
            b: `${(after.deductionBp / 100).toFixed(2)} %`,
            changed: before.deductionBp !== after.deductionBp,
          },
          {
            label: 'Payable',
            a: fmt.weight(before.payableG),
            b: fmt.weight(after.payableG),
            changed: before.payableG !== after.payableG,
          },
        ]),
    {
      label: 'Lot value',
      a: fmt.money(before.amountPaise),
      b: fmt.money(after.amountPaise),
      changed: before.amountPaise !== after.amountPaise,
    },
  )
  return (
    <div className="flex flex-col gap-3" aria-live="polite">
      <table className="w-full text-[14px]">
        <caption className="sr-only">Effect of the correction</caption>
        <thead>
          <tr className="font-mono text-[10.5px] uppercase tracking-label text-muted">
            <th scope="col" className="pb-1 text-left font-bold">
              Figure
            </th>
            <th scope="col" className="pb-1 text-right font-bold">
              Now
            </th>
            <th scope="col" className="w-6" aria-hidden="true" />
            <th scope="col" className="pb-1 text-right font-bold">
              After
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-t border-rule/70">
              <th scope="row" className="py-1.5 text-left font-normal text-ink-soft">
                {r.label}
              </th>
              <td className="py-1.5 text-right font-mono tabular text-muted">{r.a}</td>
              <td className="text-center text-muted" aria-hidden="true">
                <ArrowRight size={12} className="inline" />
              </td>
              <td
                className={cn(
                  'py-1.5 text-right font-mono tabular',
                  r.changed ? 'font-semibold text-ink' : 'text-muted',
                )}
              >
                {r.b}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        className={cn(
          'flex items-baseline justify-between rounded-card border px-3 py-2.5',
          adjustmentPaise > 0 && 'border-ok/30 bg-ok-wash',
          adjustmentPaise < 0 && 'border-danger/30 bg-danger-wash',
          adjustmentPaise === 0 && 'border-rule bg-paper-sunk',
        )}
      >
        <span className="text-[14px] text-ink">
          {adjustmentPaise > 0
            ? 'Farmer gets more'
            : adjustmentPaise < 0
              ? 'Farmer was paid too much'
              : 'No money change'}
        </span>
        <span className="font-display text-[22px] tabular text-ink" data-testid="adjustment">
          {adjustmentPaise > 0 ? '+' : ''}
          {fmt.money(adjustmentPaise)}
        </span>
      </div>
      <Banner tone={preview.payoutAction === 'AWAITING_BANK' ? 'warn' : 'info'} title="Payment">
        {PAYOUT_ACTION_TEXT[preview.payoutAction]}
      </Banner>
      {preview.errors.length > 0 && (
        <ul className="list-disc pl-5 text-[13px] text-danger">
          {preview.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
